import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, useFormikContext } from 'formik';
import { Select, Label, Popover, Button, Icon } from '@brighterion/br-ui';
import { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import {
    getEmptyStringReplacementText,
    RQ_RULE_GROUPS,
    RQ_GLOBAL_RULES,
    AVOID_INPUT_PRIORITY,
    EXECUTION_ORDER_MAX_DIGIT,
    handleKeyEvent,
    handleOnClickEvent
} from 'Utils';
import { RoleService } from 'Services';
import {
    getGroup,
    shouldShowMerchants,
    shouldShowExecutionOrder,
    shouldShowTenantGroups,
    shouldRuleGroupConditions
} from 'Selectors';
import { RULE_INPUT_PARAMETER_NAMES } from 'Common/enums';
import { useSelector } from 'react-redux';
import useGetMerchantOverrides from 'Hooks/query-hooks/merchants/useGetMerchantOverrides';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetTenantGroupOverrideRules from 'Hooks/query-hooks/tenant/useGetTenantGroupOverrideRules';

import { FormikRuleToggle, FormikInputField } from 'Components/FormikComponents';
import useAddGroups from 'Hooks/query-hooks/groups/useAddGroup';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetRuleGroups from 'Hooks/query-hooks/global-rules/useGetRuleGroups';
import CreateEditGroupModal from 'Components/CreateEditGroup/CreateEditGroupModal';
import { HandleBlur, HandleChange } from 'Components/FormikComponents/types';
import FormikRadioGroupYesNo from 'Components/FormikComponents/FormikRadioGroupYesNo';
import RevisionHistoryModal from 'Components/RevisionHistoryModal';
import RouteLoading from 'Components/RouteLoading';
import styles from './AddRuleInfoOverview.css';
import AddRuleOverview from '../AddRuleOverview/AddRuleOverview';

type OverrideSubject = 'merchant' | 'tenantGroup';

type Props = {
    mode: string;
    ruleType: 'advanced' | 'simple';
    listData: ListManagementApiList[];
    selectedRowId?: string | string[] | null;
    disabledInputFields: DisabledInputFields;
};

type GroupData = {
    key: string;
    displayValue: string;
};
const yesText = 'yes';
const noText = 'no';
const hiddenYesText = 'Yes';
const hiddenNoText = 'No';

const genGlobalRuleOverviewTop = (
    t: TFunction,
    ruleType: 'advanced' | 'simple',
    handleChange: HandleChange,
    handleBlur: HandleBlur,
    data: any,
    isThresholdEditorOnly: boolean,
    isSubmitting: boolean,
    createNewGroup: () => void,
    mode: string,
    disabledInputFields: DisabledInputFields,
    showExecutionOrder?: boolean
) =>
    [
        {
            id: RULE_INPUT_PARAMETER_NAMES.RULE_ID,
            label: t('Rule ID'),
            data: (
                <Popover
                    position='top-end'
                    size='small'
                    popperClassName={isThresholdEditorOnly ? styles.popperStyle : styles.hide}
                    contentClassName={styles.popOverContentStyle}
                    arrowClassName={styles.popperArrowStyle}
                    showArrow
                    showOnHover={isThresholdEditorOnly}
                    content={t('editProhibited')}
                >
                    <FormikInputField
                        label={t('Rule ID')}
                        name={RULE_INPUT_PARAMETER_NAMES.RULE_ID}
                        className={showExecutionOrder ? styles.ruleField : styles.ruleFieldExtended}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        disabled={isSubmitting || isThresholdEditorOnly || mode === 'edit'}
                        optionalMsg={mode === 'add' ? t('addRuleUnique') : undefined}
                    />
                </Popover>
            )
        },
        {
            id: RULE_INPUT_PARAMETER_NAMES.GROUP,
            label: t('Rule Group'),
            data: (
                <Popover
                    position='top-end'
                    size='small'
                    popperClassName={isThresholdEditorOnly ? styles.popperStyle : styles.hide}
                    contentClassName={styles.popOverContentStyle}
                    arrowClassName={styles.popperArrowStyle}
                    showArrow
                    showOnHover={isThresholdEditorOnly}
                    content={t('editProhibited')}
                >
                    <div className={styles.groupsContainer}>
                        <Field name={RULE_INPUT_PARAMETER_NAMES.GROUP}>
                            {({ field, form }: FieldProps) => {
                                return (
                                    <Select
                                        label={t('Rule Group')}
                                        className={styles.groupField}
                                        size='sm'
                                        placeholder='- Select Group -'
                                        handleClickCallback={(value) => {
                                            form.setFieldValue(field.name, value);
                                        }}
                                        selectedItems={field.value ?? []}
                                        disabled={
                                            isSubmitting ||
                                            isThresholdEditorOnly ||
                                            disabledInputFields.disableDefinitionChange
                                        }
                                    >
                                        {data.map((o: any) => {
                                            return (
                                                <option key={o?.key} value={o?.key}>
                                                    {o?.displayValue}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                );
                            }}
                        </Field>
                        {!isThresholdEditorOnly &&
                        !disabledInputFields.disableDefinitionChange &&
                        !shouldRuleGroupConditions() ? (
                            // TODO: re-enable this when we want to support adding groups in UI
                            <Button
                                dataTestId='create-new-group-btn'
                                variant='tertiary'
                                size='medium'
                                // onKeyDown={handleKeyEvent(createNewGroup)}
                                onClick={handleOnClickEvent(createNewGroup)}
                                className={styles.createNewGroupButton}
                            >
                                {t('createNewGroup')}
                            </Button>
                        ) : null}
                    </div>
                </Popover>
            )
        },
        ...(showExecutionOrder
            ? [
                  {
                      id: RULE_INPUT_PARAMETER_NAMES.PRIORITY,
                      label: t('executionOrder'),
                      data: (
                          <Popover
                              position='top-end'
                              size='small'
                              popperClassName={isThresholdEditorOnly ? styles.popperStyle : styles.hide}
                              contentClassName={styles.popOverContentStyle}
                              arrowClassName={styles.popperArrowStyle}
                              showArrow
                              showOnHover={isThresholdEditorOnly}
                              content={t('editProhibited')}
                          >
                              <Field name={RULE_INPUT_PARAMETER_NAMES.PRIORITY}>
                                  {({ field, form }: FieldProps) => {
                                      return (
                                          <FormikInputField
                                              label={t('executionOrder')}
                                              name={RULE_INPUT_PARAMETER_NAMES.PRIORITY}
                                              className={styles.executionOrder}
                                              handleChange={(event: React.ChangeEvent<any>) => {
                                                  const { value } = event.target;
                                                  const input =
                                                      value && Number(value?.slice(0, EXECUTION_ORDER_MAX_DIGIT));
                                                  form.setFieldValue(field.name, input ?? '');
                                              }}
                                              handleKeyDown={(event: React.KeyboardEvent) =>
                                                  AVOID_INPUT_PRIORITY.includes(event.key) && event.preventDefault()
                                              }
                                              handleBlur={handleBlur}
                                              disabled={
                                                  isSubmitting ||
                                                  isThresholdEditorOnly ||
                                                  disabledInputFields.disableDefinitionChange
                                              }
                                              type='number'
                                              min={1}
                                          />
                                      );
                                  }}
                              </Field>
                          </Popover>
                      )
                  }
              ]
            : []),
        {
            id: 'allowOverrides',
            label: t('Allow Tenant Overrides'),
            data: (
                <div>
                    <div className={styles.allowOverridesTitleRow}>
                        <div className={styles.allowOverridesTitle}>
                            <Label label={t('Allow Tenant Overrides')} id='allowOverrides' />
                        </div>
                        <Popover
                            popperClassName={styles.popper}
                            theme='light'
                            showArrow
                            content={
                                <div>
                                    {ruleType === 'simple'
                                        ? t('allowOverridesTooltipTextSimpleRule')
                                        : t('allowOverridesTooltipTextAdvancedRule')}
                                </div>
                            }
                            isAltText
                            contentClassName={styles.overrideTooltip}
                            arrowClassName={styles.overridePopperArrowStyle}
                        >
                            <Icon
                                name='small-help'
                                size='md'
                                className={styles.overrideTooltipIcon}
                                aria-label='help'
                            />
                        </Popover>
                    </div>
                    <FormikRadioGroupYesNo
                        name={RULE_INPUT_PARAMETER_NAMES.ALLOW_OVERRIDES}
                        disabled={isSubmitting || disabledInputFields.disableOverrideChange}
                        yesText={yesText}
                        noText={noText}
                    />
                </div>
            ),
            hide: disabledInputFields.hideOverrideChange
        }
    ].filter((entry) => {
        if ('hide' in entry) {
            return !entry.hide;
        }
        return true;
    });

const genGlobalRuleOverviewMiddle = (
    t: TFunction,
    isSubmitting: boolean,
    overrideSubjectIds: string[],
    overrideSubject: OverrideSubject,
    selectedRowId?: string | string[] | null,
    initialValues?: RuleInfoFormSchema
) => {
    const subjectOverridesCopiedMessage = 'Copied';
    let subjectOverridesLabel = '';
    let subjectOverridesNumberMessage = '';
    switch (overrideSubject) {
        case 'merchant':
            subjectOverridesLabel = 'Merchant Overrides';
            subjectOverridesNumberMessage = 'Number of Merchants';
            break;
        case 'tenantGroup':
            subjectOverridesLabel = 'Tenant Group Overrides';
            subjectOverridesNumberMessage = 'Number of Tenant Groups';
            break;
        default:
            break;
    }
    return [
        {
            id: 'status',
            label: t('Status'),
            data: (
                <div className={styles.statusField}>
                    <Label label={t('Status')} id='status' />
                    <FormikRuleToggle
                        name={RULE_INPUT_PARAMETER_NAMES.ACTIVE}
                        text={t('Rule is')}
                        disabled={isSubmitting}
                    />
                </div>
            )
        },
        {
            id: 'SubjectOverrides',
            label: t(subjectOverridesLabel),
            data:
                overrideSubject === 'tenantGroup' && !shouldShowTenantGroups() ? (
                    <></>
                ) : (
                    <div className={styles.subjectOverridesContainer}>
                        <Label label={t(subjectOverridesLabel)} id='SubjectOverrides' />
                        {overrideSubjectIds.length > 0 ? (
                            <Popover
                                showCopy
                                showOnHover
                                showArrow
                                theme='light'
                                size='small'
                                content={overrideSubjectIds.join(', ')}
                                copiedMessage={t(subjectOverridesCopiedMessage)}
                                popperClassName={styles.subjectOverridesPopper}
                            >
                                <span id='copiedSubjects' className={styles.subjectOverridesText}>
                                    {t(subjectOverridesNumberMessage, { count: overrideSubjectIds.length })}
                                </span>
                            </Popover>
                        ) : (
                            <Label label={t(subjectOverridesNumberMessage, { count: 0 })} id='noOverrides' />
                        )}
                    </div>
                )
        },
        {
            id: 'lastUpdated',
            label: t('Last Updated'),
            data: (
                <div className={selectedRowId ? styles.lastUpdatedField : styles.hide}>
                    <Label label={t('Last Updated')} id='lastUpdated' className={styles.lastUpdatedLabel} />
                    <div className={styles.lastUpdatedText}>
                        {initialValues?.createdAt &&
                            initialValues?.createdBy &&
                            getEmptyStringReplacementText(`${initialValues?.createdAt} by ${initialValues?.createdBy}`)}
                    </div>
                </div>
            )
        }
    ];
};
const genGlobalRuleOverviewBottom = (
    t: TFunction,
    isThresholdEditorOnly: boolean,
    isSubmitting: boolean,
    disabledInputFields: DisabledInputFields,
    handleChange: HandleChange,
    handleBlur: HandleBlur,
    initialValues: RuleInfoFormSchema
) => {
    const HiddenFromTenantTooltipText = t('hideFromTenantToolTipText');
    const allowHideFromTenant = disabledInputFields.disableOverrideChange && initialValues?.allowOverrides;
    return [
        {
            id: RULE_INPUT_PARAMETER_NAMES.DESCRIPTION,
            label: t('Rule Description'),
            data: (
                <Popover
                    position='top'
                    size='small'
                    popperClassName={isThresholdEditorOnly ? styles.popperStyle : styles.hide}
                    contentClassName={styles.popOverContentStyle}
                    arrowClassName={styles.popperArrowStyle}
                    showArrow
                    showOnHover={isThresholdEditorOnly}
                    content={t('editProhibited')}
                >
                    <FormikInputField
                        label={t('Rule Description')}
                        name={RULE_INPUT_PARAMETER_NAMES.DESCRIPTION}
                        className={styles.descriptionField}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        disabled={isSubmitting || isThresholdEditorOnly || disabledInputFields.disableDefinitionChange}
                    />
                </Popover>
            )
        },
        {
            id: 'hiddenFromTenant',
            label: t('hideRuleFromTenant'),
            data: (
                <div>
                    <div className={styles.allowOverridesTitleRow}>
                        <div className={styles.allowOverridesTitle}>
                            <Label label={t('hideRuleFromTenant')} id='hiddenFromTenant' />
                        </div>
                        <Popover
                            position='top'
                            popperClassName={styles.popper}
                            theme='light'
                            showArrow
                            content={<div>{HiddenFromTenantTooltipText}</div>}
                            isAltText
                            contentClassName={styles.hiddenFromTenantsToolTip}
                            arrowClassName={styles.overridePopperArrowStyle}
                        >
                            <Icon
                                name='small-help'
                                size='md'
                                className={styles.overrideTooltipIcon}
                                aria-label='help'
                            />
                        </Popover>
                    </div>
                    <FormikRadioGroupYesNo
                        name={RULE_INPUT_PARAMETER_NAMES.HIDDEN_FROM_TENANTS}
                        disabled={isSubmitting || allowHideFromTenant}
                        yesText={hiddenYesText}
                        noText={hiddenNoText}
                    />
                </div>
            ),
            hide: disabledInputFields.hideOverrideChange
        }
    ].filter((entry) => {
        if ('hide' in entry) {
            return !entry.hide;
        }
        return true;
    });
};

const AddRuleInfoOverview = ({ mode, ruleType, listData, selectedRowId, disabledInputFields }: Props) => {
    const {
        initialValues,
        setFieldValue,
        handleChange,
        handleBlur,
        isSubmitting
    } = useFormikContext<NewRuleFormSchema>();

    const { t } = useTranslation();
    const showExecutionOrder = shouldShowExecutionOrder();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const [isAddGroupModalOpen, setAddGroupModalOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const ruleGroupsAPI = useGetRuleGroups();
    const { data: merchantOverrides } = useGetMerchantOverrides();
    const { data: landlordInfo } = useGetLandlordInfo();
    const isLandlord = !!landlordInfo?.isLandlord;
    const overrideSubject: OverrideSubject = isLandlord ? 'tenantGroup' : 'merchant';
    const { data: tenantGroupOverrides } = useGetTenantGroupOverrideRules(overrideSubject === 'tenantGroup');
    const overrideTenantGroupIds =
        tenantGroupOverrides && initialValues?.ruleId ? tenantGroupOverrides[initialValues?.ruleId] || [] : [];
    const overrideMerchantIds =
        merchantOverrides && initialValues?.ruleId ? merchantOverrides[initialValues?.ruleId] || [] : [];
    const overrideSubjectIds = isLandlord ? overrideTenantGroupIds : overrideMerchantIds;

    const { addAlert } = useAlertBanner();
    const queryClient = useQueryClient();
    const group = useSelector(getGroup);

    const createNewGroup = () => {
        setAddGroupModalOpen(true);
    };

    const mutation = useAddGroups({
        mutationOptions: {
            onSuccess: (response: any, groups: Group[]) => {
                if (response.status === 201) {
                    addAlert({
                        id: 'create-group-success',
                        content: t('ruleGroupAddedSuccessfully', {
                            groupName: groups[0].name
                        }),
                        status: 'success'
                    });
                    setAddGroupModalOpen(false);
                    queryClient.invalidateQueries([RQ_RULE_GROUPS]);
                    queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId]);
                }
            },
            onError: (err: any) => {
                const statusText = err?.statusText || t('Try Later');
                addAlert({
                    id: 'create-group-success',
                    content: statusText,
                    status: 'danger'
                });
                setAddGroupModalOpen(false);
            }
        }
    });

    const { isThresholdEditorOnly } = RoleService;

    useEffect(() => {
        if (group && ruleGroupsAPI.data) {
            const groupId = ruleGroupsAPI.data.find((item: GroupData) => item.displayValue === group)?.key;
            if (groupId) {
                setFieldValue('group', [groupId]);
            }
        }
    }, [group, ruleGroupsAPI.data, setFieldValue]);

    if (ruleGroupsAPI.isError) {
        return (
            <div>
                <p>{t('Error Fetching', { fieldName: 'rule group types' })}</p>
            </div>
        );
    }

    if (ruleGroupsAPI.isLoading || ruleGroupsAPI.isFetching) {
        return <RouteLoading />;
    }
    let middleData = genGlobalRuleOverviewMiddle(
        t,
        isSubmitting,
        overrideSubjectIds,
        overrideSubject,
        selectedRowId,
        initialValues
    );

    const bottomData = genGlobalRuleOverviewBottom(
        t,
        isThresholdEditorOnly,
        isSubmitting,
        disabledInputFields,
        handleChange,
        handleBlur,
        initialValues
    );
    if ((!shouldShowMerchants() && overrideSubject === 'merchant') || mode !== 'edit') {
        middleData = middleData.filter((obj) => obj.id !== 'SubjectOverrides');
    }

    const handleRevisionHistory = () => {
        setOpenModal(true);
    };

    return (
        <>
            {isAddGroupModalOpen ? (
                <CreateEditGroupModal
                    isOpen={isAddGroupModalOpen}
                    closeModal={() => {
                        setAddGroupModalOpen(false);
                    }}
                    mutation={mutation}
                    existingGroups={
                        ruleGroupsAPI.data
                            ? ruleGroupsAPI.data.map((currentGroup: GroupData) => currentGroup.displayValue)
                            : []
                    }
                />
            ) : null}
            <AddRuleOverview
                dataTestId='add-rule-info-overview'
                leftTitle='Rule Information'
                rightTitle={
                    mode === 'edit' ? (
                        <div
                            role='button'
                            tabIndex={0}
                            onKeyDown={handleKeyEvent(handleRevisionHistory)}
                            onClick={handleOnClickEvent(handleRevisionHistory)}
                        >
                            View Revision History
                        </div>
                    ) : null
                }
                topData={genGlobalRuleOverviewTop(
                    t,
                    ruleType,
                    handleChange,
                    handleBlur,
                    ruleGroupsAPI.data,
                    isThresholdEditorOnly,
                    isSubmitting,
                    createNewGroup,
                    mode,
                    disabledInputFields,
                    showExecutionOrder
                )}
                // description, overrides
                bottomData={bottomData}
                middleData={middleData}
                isLandlord={isLandlord}
            />
            {mode === 'edit' && (
                <RevisionHistoryModal
                    ruleId={initialValues?.ruleId ?? ''}
                    level='ACQUIRER'
                    title={t('Rule Revision History')}
                    subjectIdentifier={undefined}
                    listData={listData}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                />
            )}
        </>
    );
};

export default AddRuleInfoOverview;
